import { Component } from '@angular/core';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { DataAccessService } from 'src/app/shared/services/data-access/data-access.service';
import { FilterRouteState, LocalStorage } from '../../entity/enums';
import { FilterAppliedChanges, RouteStateMeta, SelectionOption } from '../../entity/utility-interfaces';
import { DataRepositoryService } from '../../services/data-repository/data-repository.service';
import { Teams } from '../../models/business-location-details';
import { CommonService } from '../../services/common/common.service';
import { CustomRoutePreferenceService } from 'src/app/modules/setting/custom-route-preference/custom-route-preference.service';
import { take } from 'rxjs';

@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    styleUrls: [
        './filter.component.scss',
        '../../css/primeng-multiselect.component.scss',
        '../../css/primeng-dropdown.component.scss',
        '../../css/primeng-datepicker.component.scss',
        '../../css/primeng-selectbutton.component.scss'
    ],
    standalone: false
})
export class FilterComponent {

  //Full list of data variables 
  businessLocationsList: SelectionOption[] = [];
  blTeamsList: SelectionOption[] = [];
  blRoutesList: SelectionOption[] = [];

  //active BL's team and route collection variables
  storedBLTeamsList: any[] = [];
  storedBLRoutesList: any[] = [];
  storedSelectedBlTeams: string[] = [];
  storedSelectedBlRoutes: string[] = [];
  workshiftPatternList: any = [];

  //selected options
  selectedBLId = '';
  selectedBLObject: SelectionOption | undefined;
  selectedBlTeams: string[] = [];
  selectedBlRoutes: string[] = [];
  selectedWorkshiftPatterns: string[] = [];
  selectedPreferenceId: string = '';
  private storedPreferenceRoutes: string[] = [];
  filteredPreferenceOptions: SelectionOption[] = [];
  selectedPreferenceOption: SelectionOption | undefined;
  display: boolean = false;
  isFilterDisabled: boolean = true;
  selectedDate: Date;
  minDate: Date;
  maxDate: Date;
  filterIcon: string = "pi pi-filter-slash";
  routeStates: RouteStateMeta[] = [
    { state: FilterRouteState.AllRoutes, isSelected: true, label: 'All Routes', apiState: 'All' },
    { state: FilterRouteState.Sorting, isSelected: false, label: 'Sorting', apiState: 'Sorting' },
    { state: FilterRouteState.Loading, isSelected: false, label: 'Loading', apiState: 'Loading' },
    { state: FilterRouteState.OnRoute, isSelected: false, label: 'On Route', apiState: 'OnRoute' }
  ]
  selectedRouteState: any = { state: FilterRouteState.AllRoutes, isSelected: true, label: 'All Routes', apiState: 'All' };
  appliedFilterChanges: FilterAppliedChanges =  {
    isBlChanged: false,
    isTeamChanged: false,
    isRouteChanged: false,
    isRouteStateChanged: false,
    isCalendarDateChanged: false,
    isWorkshiftPatternChanged: false
  };
  isClearAllApplied: boolean = false;

  constructor(
    public dataAccessService: DataAccessService
    , public authService: AuthService
    , private commonService: CommonService
    , private dataRepositoryService: DataRepositoryService
    , private customRoutePreferenceService: CustomRoutePreferenceService
  ) { }

  //Component Lifecycle hooks

  ngOnInit(): void {
    this.getBLData();
    this.updateFilteredPreferenceOptions();
    this.updateSelectedPreferenceOption();
  }
  /**
   * Updates the filteredPreferenceOptions variable from the service.
   */
  updateFilteredPreferenceOptions() {
    this.filteredPreferenceOptions = this.customRoutePreferenceService.filteredPreferenceOptions();
  }

  /**
   * Updates the selectedPreferenceOption variable based on selectedPreferenceId and filteredPreferenceOptions.
   */
  updateSelectedPreferenceOption() {
    if (!this.selectedPreferenceId) {
      this.selectedPreferenceOption = undefined;
      return;
    }
    this.selectedPreferenceOption = this.filteredPreferenceOptions.find(option => option.id === this.selectedPreferenceId);
  }


  //Public methods

  getBLData(): void{
    this.dataAccessService.getBLData().subscribe(
      (res) => {
        this.businessLocationsList = res;
        const bl: any = localStorage.getItem(LocalStorage.BusinessLocation)
        if (!!bl) {
          this.selectedBLId = '' + JSON.parse(bl) + '';
        }
        this.isFilterApplied();

        this.getStoredValues();
        //set the filter icon view 
        this.setFilterIcon();
        this.dataAccessService.isBLSelected.next(true);
      }
    );
  }

  onBLSelection(selectedValue: any) {
    //check if a new business location is selected then clear teams and routes values
    if (selectedValue != this.selectedBLId) {
      this.blTeamsList = [];
      this.blRoutesList = [];
      this.selectedBlTeams = [];
      this.selectedBlRoutes = [];
      this.appliedFilterChanges.isBlChanged = true;
      this.selectedWorkshiftPatterns = [];
    }
    //enable the filter button
    this.isFilterDisabled = false;
    this.selectedBLId = selectedValue;
    this.selectedBLObject = this.businessLocationsList.find(f=>f.id===this.selectedBLId.toString());
    this.selectedPreferenceId = '';
    this.loadRoutePreferences(this.selectedBLId); 
    /**
     * To check if clearAllSelection is triggered, then cache value 
     * of dateoffset should NOT be read, instead selected date value should
     * be considered.
     */
   

    let dateOffset = this.calculateDateOffset()

    this.getTeamAndRouteData(this.selectedBLId, dateOffset);
    this.getWorkshiftDetails(this.selectedBLId, dateOffset);

  }

  /**
   * Method clears the selection from all controls
   */
  onClearSelection() {
    this.blTeamsList = [];
    this.blRoutesList = [];
    this.selectedBLId = '';
    this.selectedBlTeams = [];
    this.selectedBlRoutes = [];
    this.selectedWorkshiftPatterns = [];
    this.selectedRouteState = this.routeStates[0];
    this.selectedBLObject = {} as SelectionOption;
    this.selectedDate = new Date();
    this.isFilterDisabled = true;
    this.isClearAllApplied = true;
    this.selectedPreferenceId = '';
    this.storedPreferenceRoutes = [];
    localStorage.removeItem(LocalStorage.SelectedUserPreference);
    this.filteredPreferenceOptions = [];
    this.selectedPreferenceOption = undefined;
  }

  /**
   * Method gets the Teams and Route data for selected Business location and optional 
   * dateoffset
   * @param blId selected business location id
   * @param dateOffset optional param - selected calendar date 
   */
  getTeamAndRouteData(blId: string, dateOffset?: number){
    this.dataAccessService.getBLTeamsRouteList(blId, dateOffset).subscribe({
      next: (res) => {
        //First Index of the response is costCenter data and Second is Routes data
        
        if(res){
          this.storedBLTeamsList = [...res[0]];
          this.blTeamsList = [];
          this.blTeamsList = [...res[0]].map((team: any) => ({
            id: team.cost_center_id, value: team.name, isDisabled: false
          }));
  
          //routes
          this.storedBLRoutesList = [...res[1]];
          this.blRoutesList = [];
          
          this.blRoutesList = [...res[1]].map((route: any) => ({
            id: route.route_label, value: route.route_label, isDisabled: false
          }));

          if (!this.appliedFilterChanges.isBlChanged && !this.appliedFilterChanges.isCalendarDateChanged) {
            this.getStoredSelectedTeamValues();
            this.getStoredSelectedRouteValue();
            this.mergePreferenceRoutes();
            this.setTeamRouteAssociationValues()
          }
        }

      }
      ,error:(err:any)=>{}
      ,complete: ()=>{}
    })
  }

  onTeamsSelection(selectedValue: any) {
    if(selectedValue.length >= this.selectedBlTeams.length){
      //get the cost_center_id from the selected Value and check the route association
      this.checkTeamRouteAssociation(selectedValue[selectedValue.length-1]);
    }
    else if(selectedValue.length < this.selectedBlTeams.length){
      this.setTeamRouteDisassociation(this.selectedBlTeams.filter(fl=>!selectedValue.find((f:any)=>f==fl))[0]);
    }

    this.selectedBlTeams = selectedValue;
    this.appliedFilterChanges.isTeamChanged = true;
    this.isFilterDisabled = false;
  }

  onWorkshiftPatternSelection(selectedValue:any){
    this.selectedWorkshiftPatterns = selectedValue??[];
    this.appliedFilterChanges.isWorkshiftPatternChanged = true;
    this.isFilterDisabled = false;
  }

  onRouteSelection() {
    this.appliedFilterChanges.isRouteChanged = true;
    this.isFilterDisabled = false
  }

  onRouteStateChange(){
    if (!!this.selectedBLObject?.id && this.selectedBLObject?.id !=='') {
      this.isFilterDisabled = false
    }
  }
  
  onDateSelection(){
    if (!!this.selectedBLObject?.id && this.selectedBLObject?.id !=='') {
      this.appliedFilterChanges.isCalendarDateChanged = true;
      this.isFilterDisabled = false;

      //clear the selected route and selected teams 
      this.blTeamsList = [];
      this.blRoutesList = [];
      this.selectedBlTeams = [];
      this.selectedBlRoutes = [];
      this.selectedWorkshiftPatterns = [];
      this.selectedPreferenceId = '';
      this.storedBLRoutesList = [];
      this.storedBLTeamsList = [];
      this.workshiftPatternList = [];
      this.storedPreferenceRoutes = [];
      this.selectedPreferenceOption = undefined;
      //call the new teams and route list for new date
      let selectedDateOffset = this.calculateDateOffset();
      this.getTeamAndRouteData(this.selectedBLId, selectedDateOffset);
      this.getWorkshiftDetails(this.selectedBLId, selectedDateOffset);
    }
  }

  /**
   * Method calculates the dateoffet value by comparing months and date of the 
   * selected date from calendar and current system date.
   * @returns calculated dateoffset value 
   */
  calculateDateOffset(){
    let dateOffset = 0;
    let currentDate = new Date();
    let selectedDate = this.selectedDate;
    // Calculate the difference in total days between the two dates
    dateOffset = Math.ceil((selectedDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
    return dateOffset;
  }

  checkTeamRouteAssociation(cost_center_id: string) {
    const matchedRoutes = this.storedBLRoutesList.filter(r => r.cost_center_id === cost_center_id);
    const associatedRoutes = matchedRoutes.map((route: any) => ({
      id: route.route_label,
      value: route.route_label,
      isDisabled: true
    }));
    const associatedRouteValues = associatedRoutes.map(r => r.value);

    // Remove any already-selected routes belonging to this team to avoid duplicates on re-selection
    this.selectedBlRoutes = this.selectedBlRoutes.filter(selRoute => !associatedRouteValues.includes(selRoute));

    // Remove routes that no longer exist in the current BL route list
    this.checkSelectedRouteAvailability();

    // Add all team-associated routes to the selection
    this.selectedBlRoutes = [...this.selectedBlRoutes, ...associatedRouteValues];

    // Register the team-to-route association for later use (e.g. disassociation, filter apply)
    if (matchedRoutes.length) {
      this.dataRepositoryService.selectedTeamRouteAssociation.set(cost_center_id, matchedRoutes);
    }

    // Disable associated routes in the dropdown so the user cannot manually deselect them
    this.blRoutesList.forEach(route => {
      route.isDisabled = associatedRoutes.some(r => r.id === route.id) ? true : route.isDisabled;
    });
  }

  /**
    * This method subscribes to get Workshift patterns and maps the data to populate for Pattern filtering dropdown
    * @param blid - current selected BL
    * @param dateOffset - selected calendar date
  */
  getWorkshiftDetails(blid: string, dateOffset: number) {
    this.dataAccessService.getWorkshiftData(blid, dateOffset).subscribe({
      next: (response: any) => {
        const workshifts = response?.business_locations[0]?.workshifts;
        this.workshiftPatternList = Array.from(
          new Map(workshifts
            .filter((data: any) => data?.pattern_id || data.pattern_name)
            .map((pattern: any) => [pattern.pattern_name, pattern])).values()).map((data: any) => ({
              value: `${data.pattern_id} ( ${data.pattern_name??''} )`,
              id: data.pattern_id
            }));
      }
    });
  }

  /**
   * Removes any selected routes that no longer exist in the current BL route list
   * (e.g. when the date changes and the API returns a different set of routes).
   */
  checkSelectedRouteAvailability() {
    const availableRouteValues = this.blRoutesList.map(r => r.value);
    this.selectedBlRoutes = this.selectedBlRoutes.filter(selRoute => availableRouteValues.includes(selRoute));
  }

  setTeamRouteDisassociation(cost_center_id:string){
    let matchedRoutes = this.storedBLRoutesList.filter(f=>f.cost_center_id===cost_center_id);
    // Get preference routes to preserve
    const prefRoutes = this.selectedPreferenceId
      ? (this.customRoutePreferenceService.filteredRoutePreferences()
          .find(p => p.id === this.selectedPreferenceId)?.routes ?? [])
      : [];
    // If route is associated with the team and in preference, 
    // then do not remove it from selected routes, else remove it.
    this.selectedBlRoutes = this.selectedBlRoutes.filter(selRoute => {
      // If the route is in the matched routes for the team being disassociated, 
      // check if it's in the preference routes. If it is, keep it selected, if not, remove it.
      if (matchedRoutes.find(matchedRoute => matchedRoute.route_label === selRoute)) {
        return prefRoutes.includes(selRoute);
      }
      return true;
    });

    //remove from Map object
    this.dataRepositoryService.selectedTeamRouteAssociation.delete(cost_center_id);

    // Re-enable routes that belonged to the deselected team in the dropdown.
    // But keep them disabled if they are still covered by the active preference.
    this.blRoutesList.forEach(route => {
      if (matchedRoutes.some(f => f.route_label == route.id)) {
        route.isDisabled = prefRoutes.includes(route.id as string);
      }
    });
  }

  applyFilter() {
    //store date offset in data repository
    this.dataRepositoryService.dateOffset = this.calculateDateOffset();

    //store route state in data repository
    this.dataRepositoryService.selectedFilteredRouteState = this.selectedRouteState.apiState;
    // set the selecte workshift patterns in data Access service to use in routeinfo API call
    this.dataAccessService.selectedWorkshiftPatterns = this.selectedWorkshiftPatterns;
    //store BL id in data repository
    let blAddress;
    if (this.selectedBLId != '') {
      this.dataRepositoryService.selectedBusinessLocation = this.selectedBLId;
      blAddress = this.dataRepositoryService.businessLocationAddress.get(this.selectedBLId);
      if(blAddress?.coordinate){
        this.dataRepositoryService.blCoordinates = blAddress?.coordinate
      }
    }
    
    let coords = blAddress?.coordinate;
    //store allvalues in local storage
    this.setLocalStorage(LocalStorage.BusinessLocation, JSON.stringify(this.selectedBLId));
    this.setLocalStorage(LocalStorage.RouteState, JSON.stringify(this.dataRepositoryService.selectedFilteredRouteState));
    this.setLocalStorage(LocalStorage.BLCoordinates, JSON.stringify(coords));

    //store teams values in local storage
    this.formatBLTeams();
    //store Route values in local storage
    this.formatBLRoutes();

    //store selected route preference and its routes in local storage
    const preferenceData = this.selectedPreferenceId
      ? {
          id: this.selectedPreferenceId,
          routes: this.customRoutePreferenceService.filteredRoutePreferences()
            .find(p => p.id === this.selectedPreferenceId)?.routes ?? []
        }
      : '';
    this.setLocalStorage(LocalStorage.SelectedUserPreference, JSON.stringify(preferenceData));

    //set the filter icon view 
    this.setFilterIcon();
    this.dataAccessService.setFilterApplied(true, this.appliedFilterChanges)
    this.resetControlSelection();
  }

  setLocalStorage(localStorageLabel:string, input:string){
    //store allvalues in local storage
    localStorage.setItem(localStorageLabel, input);
  }

  closePopup() {
    this.resetControlSelection();
  }

  resetControlSelection(){
    this.display = false;
    this.isFilterDisabled = true;
    this.appliedFilterChanges = {
      isBlChanged:false,
      isCalendarDateChanged: false,
      isRouteChanged: false,
      isRouteStateChanged: false,
      isTeamChanged: false,
      isWorkshiftPatternChanged: false
    };
    this.selectedBLId = '';
    this.selectedBlTeams = []
    this.selectedBlRoutes = [];
  }

  getStoredValues() {
    this.getDate();
    this.minDate = new Date();
    this.maxDate = new Date();

    this.minDate = new Date(this.minDate.setDate(new Date().getDate() - 5));
    this.maxDate = new Date(this.maxDate.setDate(new Date().getDate() + 1));
    this.getRouteState();
    //store date offset in data repository
    this.dataRepositoryService.dateOffset = (this.selectedDate.getDate() - (new Date()).getDate());

    //store route state in data repository
    this.dataRepositoryService.selectedFilteredRouteState = this.selectedRouteState.apiState;

  }

  getRouteState(){
    let storedRouteState: any = localStorage.getItem(LocalStorage.RouteState);
    if (storedRouteState != null && JSON.parse(storedRouteState) != '') {
      this.selectedRouteState = this.routeStates[this.routeStates.findIndex(rs => rs.apiState.toLowerCase() == JSON.parse(storedRouteState).toLowerCase())];
    }
    else {
      this.selectedRouteState = this.routeStates[0];
    }
  }

  getDate(){
    let storedDateOffset: any = this.dataRepositoryService.dateOffset;
    if (storedDateOffset && JSON.parse(storedDateOffset) != '' && (JSON.parse(storedDateOffset) <= 1 || JSON.parse(storedDateOffset) >= -5)) {
      let date = new Date(new Date().setDate(new Date().getDate() + JSON.parse(storedDateOffset)));

      this.selectedDate = this.commonService.dateFormatter(date);
    }
    else {
      this.selectedDate = this.commonService.dateFormatter(new Date());
    }
  }
  updateRepoforBLTeams() {
    let blTeams: Map<string, Teams> = new Map<string, Teams>();
    this.storedBLTeamsList?.reduce((map: any, team) => {
      map.set(team.cost_center_id, team);
      return map;
    }, blTeams);
    if (blTeams.size) {
      this.dataRepositoryService.teamList.clear();
      this.dataRepositoryService.teamList = blTeams;

    }
  }

  updateRepoForBLRoutes(){
    if (this.storedBLRoutesList.length) {
      this.dataRepositoryService.blAllRouteList = [];
      this.dataRepositoryService.blAllRouteList = this.storedBLRoutesList.map(route => route.route_label);
    }
  }

  formatBLTeams() {
    //update BLTeams Map data set

    this.updateRepoforBLTeams();
    this.dataRepositoryService.selectedTeamList.clear();
    this.selectedBlTeams?.forEach((team: any) => {
      this.dataRepositoryService.selectedTeamList.set(team, team);
    })
    localStorage.setItem(LocalStorage.BLSelectedTeams, JSON.stringify([...this.dataRepositoryService.selectedTeamList]));
  }

  formatBLRoutes() {
    //update BLRoute Map data set
    this.updateRepoForBLRoutes();
    this.dataRepositoryService.selectedFilteredRouteList = [];
    let selectedTeamsRoutes = [...this.dataRepositoryService.selectedTeamRouteAssociation.values()];
    // Get preference routes to exclude from BLSelectedRoutes
    const prefRoutes = this.selectedPreferenceId
      ? (this.customRoutePreferenceService.filteredRoutePreferences()
          .find(p => p.id === this.selectedPreferenceId)?.routes ?? [])
      : [];
    // Exclude preference-managed routes and team-auto-assigned routes from the manual route list.
    // These are tracked separately (preference via LocalStorage.SelectedUserPreference,
    // team routes via selectedTeamRouteAssociation).
    const teamRouteLabels = selectedTeamsRoutes.flat().map((r: any) => r.route_label);
    this.dataRepositoryService.selectedFilteredRouteList = this.selectedBlRoutes.filter(route => {
      if (prefRoutes.includes(route)) return false;
      if (selectedTeamsRoutes.length) return !teamRouteLabels.includes(route);
      return true;
    });
    localStorage.setItem(LocalStorage.BLSelectedRoutes, JSON.stringify(this.dataRepositoryService.selectedFilteredRouteList.toString()));
  }

  /**
   * Merges stored preference routes into selectedBlRoutes (visual), 
   * only including routes that exist in the current BL route list.
   */
  private mergePreferenceRoutes() {
    if (!this.storedPreferenceRoutes.length) return;
    const validRoutes = this.storedPreferenceRoutes.filter(
      route => this.blRoutesList.some(r => r.value === route) && !this.selectedBlRoutes.includes(route)
    );
    this.selectedBlRoutes = [...this.selectedBlRoutes, ...validRoutes];
    // Disable preference routes in the dropdown
    this.blRoutesList.forEach(route => {
      if (this.storedPreferenceRoutes.includes(route.value)) {
        route.isDisabled = true;
      }
    });
  }

  getStoredSelectedTeamValues(){
    let teams: any = localStorage.getItem(LocalStorage.BLSelectedTeams)
    if (!!teams && JSON.parse(teams).length) {
      let teamsMapObj: any = [...(new Map(JSON.parse(teams))).values()];
      this.selectedBlTeams = teamsMapObj;

    }
    else{
      this.selectedBlTeams = [];
    }
  }

  getStoredSelectedRouteValue(){
    let routes: any = localStorage.getItem(LocalStorage.BLSelectedRoutes);
    if (!!routes && JSON.parse(routes) != '') {
      let routesMapObj: string[] = JSON.parse(routes).split(',').map((route: any) => (route));
      this.selectedBlRoutes = [];
      this.selectedBlRoutes = routesMapObj;
    }
    else {
      this.selectedBlRoutes = [];
    }
  }

  /**
   * check the associat?ed routes and make those route disable in routes dropdown
   */
  setTeamRouteAssociationValues(){
    this.selectedBlTeams?.forEach(selTeam=>this.checkTeamRouteAssociation(selTeam)); 
  }

  isFilterApplied() {
    if (this.selectedBLId !== '') {
      this.blTeamsList = [];
      this.blTeamsList = [{
        id: '', value: ''
      }]
      this.blRoutesList = [];

      if (!this.selectedBlTeams.length) {
        let teams: any = localStorage.getItem(LocalStorage.BLSelectedTeams)
        if (!!teams && JSON.parse(teams).length) {
          let teamsMapObj: any = [...(new Map(JSON.parse(teams))).values()];
          this.selectedBlTeams = teamsMapObj;
        }
      }
      if (!this.selectedBlRoutes.length) {
        let routes: any = localStorage.getItem(LocalStorage.BLSelectedRoutes);
        if (!!routes && JSON.parse(routes) != '') {
          let routesMapObj: string[] = JSON.parse(routes).split(',').map((route: any) => (route));
          this.selectedBlRoutes = routesMapObj;
        }
      }
    }
    else {
      this.filterIcon = "pi pi-filter-slash";
      this.showDialog()
    }
  }

  showDialog() {
    this.display = true;
    this.setSelectionValues();
  }

  setSelectionValues() {
    this.selectedBLObject = {} as SelectionOption;
    this.selectedBlRoutes = [];
    this.selectedBlTeams = [];
    if (this.dataRepositoryService?.businessLocationListMap.size) {
      this.businessLocationsList = [];
      this.businessLocationsList = [...this.dataRepositoryService.businessLocationListMap.values()].reduce((acc: any, bl: any) => {
        acc.push({ id: bl.business_location_id, value: bl.name });
        return acc;
      }, [])
    }
    
    const bl: any = localStorage.getItem(LocalStorage.BusinessLocation);
    if (!!bl) {
      let blid = JSON.parse(bl)
      this.selectedBLObject = this.businessLocationsList.find(f=>f.id===blid.toString());
      this.selectedBLId = blid.toString();
      this.loadRoutePreferences(this.selectedBLId);
    }else{
      this.selectedBLObject = {} as SelectionOption;
      this.updateFilteredPreferenceOptions();
    }

    // Restore selected route preference from local storage
    const storedRoutePref = localStorage.getItem(LocalStorage.SelectedUserPreference);
    let restoredPrefId = '';
    let restoredPrefRoutes: string[] = [];
    if (storedRoutePref) {
      const parsed = JSON.parse(storedRoutePref);
      if (typeof parsed === 'object' && parsed?.id) {
        restoredPrefId = parsed.id;
        restoredPrefRoutes = parsed.routes ?? [];
      } else if (typeof parsed === 'string') {
        restoredPrefId = parsed;
      }
    }
    this.selectedPreferenceId = restoredPrefId;
    this.storedPreferenceRoutes = restoredPrefRoutes;
    this.updateSelectedPreferenceOption();
    // Get Workshift patterns for selected BL and selected date offset;
    this.selectedBLId ? this.getWorkshiftDetails(this.selectedBLId, this.dataRepositoryService.dateOffset) : this.getWorkshiftDetails(this.dataRepositoryService.selectedBusinessLocation, this.dataRepositoryService.dateOffset);
    this.getRouteState();
    //Set workshift patterns
    this.selectedWorkshiftPatterns = this.dataAccessService.selectedWorkshiftPatterns??[]
    this.getDate();

    //NOTE below onwards teams and route API should be called
    if(!!bl){
      this.getTeamAndRouteData(JSON.parse(bl))
    }

    //set the filter icon view 
    this.setFilterIcon();

  }

  /**
   * Loads route preferences for the given BL and updates the filter dropdown.
   */
  private loadRoutePreferences(blId: string) {
    this.customRoutePreferenceService.selectedBlIdForFilter.set(blId);
    if (!this.customRoutePreferenceService.routePreferences().length) {
      this.customRoutePreferenceService.getRoutePreferences().pipe(take(1)).subscribe(() => {
        this.updateFilteredPreferenceOptions();
        this.updateSelectedPreferenceOption();
      });
    } else {
      this.updateFilteredPreferenceOptions();
      this.updateSelectedPreferenceOption();
    }
  }

  /**
   * Handles custom route preference selection.
   * Auto-populates routes from the preference and marks filter as dirty.
   */
  onRoutePreferenceSelection(preferenceId: string | undefined) {
    if (!preferenceId) {
      this.selectedPreferenceId = '';
      this.clearPreferenceRouteDisabling();
      this.storedPreferenceRoutes = [];
    } else {
      this.clearPreferenceRouteDisabling();
      this.selectedPreferenceId = preferenceId;
      const preference = this.customRoutePreferenceService.filteredRoutePreferences()
        .find(p => p.id === preferenceId);
      this.storedPreferenceRoutes = preference?.routes ?? [];
      if (preference?.routes?.length) {
        const validPrefRoutes = preference.routes.filter(
          route => this.blRoutesList.some(r => r.value === route) && !this.selectedBlRoutes.includes(route)
        );
        if (validPrefRoutes.length) {
          this.selectedBlRoutes = [...this.selectedBlRoutes, ...validPrefRoutes];
        }
        // Disable preference routes in the dropdown
        this.blRoutesList.forEach(route => {
          route.isDisabled = preference.routes.includes(route.value) ? true : route.isDisabled;
        });
      }
      this.appliedFilterChanges.isRouteChanged = true;
    }
    this.updateSelectedPreferenceOption();
    this.isFilterDisabled = false;
  }

  /**
   * Re-enables routes that were disabled by a preference and removes them from selection.
   */
  private clearPreferenceRouteDisabling() {
    // Build the set of route labels currently locked by selected teams.
    // These must remain disabled regardless of preference changes.
    const teamLockedRouteLabels = new Set(
      (this.selectedBlTeams || []).flatMap(teamId =>
        this.storedBLRoutesList.filter(r => r.cost_center_id === teamId).map(r => r.route_label)
      )
    );

    if (!this.selectedPreferenceId) {
      // No previous preference to clear — just remove pure-preference routes from selection.
      // Keep routes that are also locked by a selected team (they stay selected + disabled).
      const prefRoutes = this.storedPreferenceRoutes ?? [];
      this.selectedBlRoutes = this.selectedBlRoutes.filter(
        r => !prefRoutes.includes(r) || teamLockedRouteLabels.has(r)
      );

      // Re-enable routes in the dropdown, but preserve the disabled state for team-locked routes.
      this.blRoutesList.forEach(route => {
        route.isDisabled = teamLockedRouteLabels.has(route.id as string);
      });

      this.selectedBlRoutes = [...this.selectedBlRoutes];
      this.storedPreferenceRoutes = [];
      return;
    }

    // A previous preference is being replaced — re-enable its routes and remove them from selection,
    // but only if those routes are not also locked by a currently selected team.
    const prevPreference = this.customRoutePreferenceService.filteredRoutePreferences()
      .find(p => p.id === this.selectedPreferenceId);
    if (prevPreference?.routes?.length) {
      this.blRoutesList.forEach(route => {
        if (prevPreference.routes.includes(route.value)) {
          route.isDisabled = teamLockedRouteLabels.has(route.id as string);
        }
      });
      this.selectedBlRoutes = this.selectedBlRoutes.filter(
        r => !prevPreference.routes.includes(r) || teamLockedRouteLabels.has(r)
      );
    }
    this.isFilterDisabled = false;
  }

  setFilterIcon(){
    if(this.selectedBLId === ""){
      this.filterIcon = "pi pi-filter-slash";
    }
    else {
      let teams: any = localStorage.getItem(LocalStorage.BLSelectedTeams);
      let routes: any = localStorage.getItem(LocalStorage.BLSelectedRoutes);
      if (!!teams && JSON.parse(teams).length
        || !!routes && JSON.parse(routes) != ''
        || this.dataRepositoryService.selectedFilteredRouteState.toLowerCase() !== 'all'
        || this.dataRepositoryService.dateOffset !== 0
      ) {
        this.filterIcon = "pi pi-filter-fill";
      }
      else{
        this.filterIcon = "pi pi-filter";
      }
    } 
  }


}
